import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useNav } from "../nav.tsx";
import { ErrorNote, errText, Field, SectionTitle, sourceDot } from "../ui.tsx";
import type { StageProps } from "./EmployeeShell.tsx";

const ACCEPT = ".pdf,.docx,.txt,.md,.markdown,.csv,.html,.htm,audio/*,video/*";

export function KnowledgeStage({ api, detail, reload }: StageProps) {
  const { go } = useNav();
  const e = detail.employee;
  const fileRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const locked = detail.stages.knowledge === "locked";

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
      await reload();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadFiles = (files: FileList | null) =>
    run(async () => {
      for (const f of Array.from(files ?? [])) await api.uploadSource(e.id, f);
      if (fileRef.current) fileRef.current.value = "";
    });

  return (
    <div className="grid gap-4">
      <SectionTitle
        title="Knowledge"
        description="Give the employee what it needs to know about the business. Everything is turned into text; the avatar reads it before the interview and asks about what is missing."
        action={
          <Button variant="outline" onClick={() => go(`/employees/${e.id}/interview`)} disabled={detail.stages.interview === "locked"}>
            Continue to interview
          </Button>
        }
      />
      {locked && <p className="text-sm text-muted-foreground">Set the name and job title first.</p>}
      <ErrorNote error={error} />

      <Card>
        <CardHeader>
          <CardTitle>Add a source</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="file">
            <TabsList>
              <TabsTrigger value="file">Document</TabsTrigger>
              <TabsTrigger value="link">Web page</TabsTrigger>
              <TabsTrigger value="video">Video</TabsTrigger>
            </TabsList>
            <TabsContent value="file" className="mt-4">
              <Field label="Upload files" htmlFor="kb-file" hint={`PDF, DOCX, TXT, Markdown, HTML, or audio/video up to ${Math.round(25)} MB.`}>
                <Input id="kb-file" ref={fileRef} type="file" multiple accept={ACCEPT} disabled={busy || locked} onChange={(x) => void uploadFiles(x.target.files)} />
              </Field>
            </TabsContent>
            <TabsContent value="link" className="mt-4">
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(x) => {
                  x.preventDefault();
                  void run(async () => {
                    await api.addLink(e.id, { kind: "link", url: url.trim() });
                    setUrl("");
                  });
                }}
              >
                <div className="min-w-64 flex-1">
                  <Field label="Page URL" htmlFor="kb-url">
                    <Input id="kb-url" type="url" placeholder="https://example.com/policies" value={url} onChange={(x) => setUrl(x.target.value)} disabled={locked} />
                  </Field>
                </div>
                <Button type="submit" disabled={busy || locked || !url.trim()}>
                  Add page
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="video" className="mt-4">
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(x) => {
                  x.preventDefault();
                  void run(async () => {
                    await api.addLink(e.id, { kind: "video", url: videoUrl.trim() });
                    setVideoUrl("");
                  });
                }}
              >
                <div className="min-w-64 flex-1">
                  <Field label="Video URL" htmlFor="kb-video" hint="YouTube links use the video's captions. Direct audio/video links up to 25 MB are transcribed.">
                    <Input id="kb-video" type="url" placeholder="https://www.youtube.com/watch?v=…" value={videoUrl} onChange={(x) => setVideoUrl(x.target.value)} disabled={locked} />
                  </Field>
                </div>
                <Button type="submit" disabled={busy || locked || !videoUrl.trim()}>
                  Add video
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {detail.sources.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.sources.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="max-w-md align-top whitespace-normal">
                  <div className="font-medium">{s.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.kind}
                    {s.originUrl ? ` · ${s.originUrl}` : ""}
                  </div>
                  {s.summary && <p className="mt-1 text-xs text-muted-foreground">{s.summary}</p>}
                  {s.error && <p className="mt-1 text-xs text-destructive">{s.error}</p>}
                </TableCell>
                <TableCell className="align-top">{sourceDot(s.status)}</TableCell>
                <TableCell className="space-x-1 text-right align-top">
                  {s.status === "failed" && (
                    <Button size="xs" variant="outline" disabled={busy} onClick={() => void run(() => api.retrySource(e.id, s.id))}>
                      Retry
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="xs" variant="ghost" disabled={busy}>
                        Remove
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove “{s.title}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                          It will no longer be part of this employee's knowledge. Releases already published keep their copy. An existing draft becomes stale.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={() => void run(() => api.deleteSource(e.id, s.id))}>
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
